/**
 * User-facing message for failed login/register API calls (axios).
 * @param {unknown} error
 * @param {(key: string, defaultValue?: string) => string} t - i18next `t`
 */
export function getAuthHttpErrorMessage(error, t) {
  const fallback = t('auth.requestFailed', 'Something went wrong. Please try again.');
  if (error == null || typeof error !== 'object') return fallback;

  const res = error.response;
  if (res && typeof res.status === 'number') {
    const { status, data } = res;
    let serverMsg = null;
    if (typeof data === 'string') serverMsg = data.trim();
    else if (data && typeof data === 'object') {
      const m = data.message ?? data.error ?? data.msg;
      if (typeof m === 'string') serverMsg = m.trim();
    }

    if (status === 429) {
      const h = res.headers || {};
      const ra = h['retry-after'] ?? h['Retry-After'];
      const sec = parseInt(String(ra ?? ''), 10);
      if (!Number.isNaN(sec) && sec > 0) {
        return t(
          'auth.rateLimitedWithRetry',
          'Too many attempts. Try again in about {{seconds}}s.',
          { seconds: sec }
        );
      }
      return t(
        'auth.rateLimited',
        'Too many attempts. Please wait a few minutes before trying again.'
      );
    }
    if (status === 401 || status === 403 || status === 410 || status === 411) {
      return t('auth.loginError', 'Login failed. Please check your credentials.');
    }
    if (status >= 500 && status < 600) {
      return t(
        'auth.serverError',
        'The server is temporarily unavailable. Please try again shortly.'
      );
    }
    if (serverMsg) return serverMsg;
  }

  if (error.code === 'ECONNABORTED') {
    return t('auth.timeout', 'The request timed out. Please try again.');
  }
  if (error.message === 'Network Error') {
    return t(
      'auth.networkError',
      'Network error. Check your connection and try again.'
    );
  }
  return fallback;
}
